/**
 * WANLE-3 · 上架中心「携程玩乐」页签（样稿 携程玩乐直连-样稿.html ⑤）：每个选项一行；VBK 后台人工建好商品后，在这里填携程编号绑定。
 *   GET /api/v1/integrations/platforms/ctrip_wanle/mappings（每行带 wanle_bindings）
 *   POST …/mappings {product_id, option_id, ctrip_product_id, package_code, plu} · PATCH …/mappings/{id} {status}
 * 只收一日游 + 门票；多日游整行置灰「走携程页签」。绑定后立即首推库存（价格默认不推）。
 */
import { Fragment, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { patchMapping } from "@/services/channelCenter";
import { ApiError } from "@/services/api";
import { wanleApi } from "@/services/ctripWanle";
import { useCan } from "@/auth/permissions";
import { WanlePriceDrawer } from "./WanlePriceDrawer";   // WANLE-PRICE-1
import type { MappingProductRow, WanleBinding } from "@/services/types/channelCenter";

const usd = (c: number | null | undefined) => (c == null ? "—" : `$${Math.round(c / 100)}`);
const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString("zh-CN", { hour12: false, month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : null);
const usd2 = (c: number | null | undefined) => (c == null ? "—" : `$${(c / 100).toFixed(2)}`);
const inp = "w-full rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 font-mono text-[13px]";

export function WanleBindModal({ row, b, onClose, onDone }: { row: MappingProductRow; b: WanleBinding; onClose: () => void; onDone: () => void }) {
  const [productNo, setProductNo] = useState(b.wanle_product_id ?? "");
  const [pkg, setPkg] = useState(b.package_code);
  const [plu, setPlu] = useState(b.plu);
  const [childPlu, setChildPlu] = useState(b.child_plu ?? (b.child_enabled ? `${b.option_code}-CHD` : ""));   // TICKETOPT-1 · 门票选项儿童 PLU，建议 选项编号-CHD
  const [err, setErr] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => wanleApi.bind({ product_id: row.product_id, option_id: b.option_id, ctrip_product_id: productNo.trim(), package_code: pkg.trim(), plu: plu.trim(), ...(b.child_enabled && childPlu.trim() ? { child_plu: childPlu.trim() } : {}) }),
    onSuccess: () => { onDone(); onClose(); },
    onError: (e) => {
      const inner = (e as { detail?: { detail?: { message?: string } } })?.detail?.detail;
      setErr(inner?.message ?? (e instanceof ApiError ? e.message : "绑定失败，请稍后再试"));
    },
  });
  return (
    <div role="dialog" aria-label="填携程编号绑定" data-testid="wanle-bind-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm">
      <div className="w-[480px] max-w-[92vw] space-y-3 rounded-xl bg-white p-5 shadow-xl">
        <h3 className="text-[15px] font-semibold text-slate-900">填携程编号绑定 · {row.product_name} · {b.option_code}</h3>
        <p className="text-[12px] text-slate-500">照携程玩乐后台（VBK）上看到的填。我们建议套餐编号 = 选项编号、PLU = 选项编号 + <span className="font-mono">-PAX</span>，在 VBK 建商品时就这样填，这里已自动带出。绑定后立即推一次库存（价格要在渠道中心配好后才推）。</p>
        <label className="block text-[12.5px] text-slate-600">携程产品号（必填）
          <input aria-label="携程产品号" value={productNo} onChange={(e) => setProductNo(e.target.value)} className={inp} placeholder="如 81234567" /></label>
        <label className="block text-[12.5px] text-slate-600">套餐编号
          <input aria-label="套餐编号" value={pkg} onChange={(e) => setPkg(e.target.value)} className={inp} /></label>
        <label className="block text-[12.5px] text-slate-600">票种 PLU
          <input aria-label="票种 PLU" value={plu} onChange={(e) => setPlu(e.target.value)} className={inp} /></label>
        {b.child_enabled && (
          <label className="block text-[12.5px] text-slate-600">儿童 PLU（选填，须以 <span className="font-mono">-CHD</span> 结尾）
            <input aria-label="儿童 PLU" data-testid="wanle-child-plu" value={childPlu} onChange={(e) => setChildPlu(e.target.value)} className={inp} placeholder={`${b.option_code}-CHD`} />
            <span className="mt-0.5 block text-[11.5px] text-slate-400">携程玩乐上这个票种的儿童票 PLU；填了就同步推儿童价和库存，不填 = 只卖成人票。</span></label>
        )}
        {err && <div data-testid="wanle-bind-err" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[12.5px] text-red-700">{err}</div>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-md border border-slate-200 px-3 py-1.5 text-[13px] text-slate-700">取消</button>
          <button type="button" data-testid="wanle-bind-save" disabled={!productNo.trim() || !plu.trim() || save.isPending} onClick={() => save.mutate()}
            className="rounded-md bg-blue-600 px-3 py-1.5 text-[13px] font-semibold text-white disabled:opacity-50">绑定</button>
        </div>
      </div>
    </div>
  );
}

export function WanleBindingTable({ rows, canManage, onChanged }: { rows: MappingProductRow[]; canManage: boolean; onChanged: () => void }) {
  const [editing, setEditing] = useState<{ row: MappingProductRow; b: WanleBinding } | null>(null);
  const [pricing, setPricing] = useState<{ row: MappingProductRow; b: WanleBinding } | null>(null);   // WANLE-PRICE-1
  const canPrice = useCan("pricing.channel_config.update");
  const unbind = useMutation({
    mutationFn: (b: WanleBinding) => patchMapping("ctrip_wanle", b.mapping_id!, { status: "inactive" }),
    onSuccess: onChanged,
  });
  return (
    <>
      <table className="w-full text-[13px]" data-testid="wanle-table">
        <thead>
          <tr className="border-b border-slate-200 text-left text-[12px] font-medium text-slate-400">
            <th className="w-11 py-2">推送</th><th>产品</th><th>选项 / 时段</th><th>携程编号（产品 / 套餐 / 票种 PLU）</th><th>渠道价/人</th><th>携程价（每人）</th><th>状态</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const bs = row.wanle_bindings ?? [];
            const blocked = bs[0]?.blocked_reason ?? null;
            const kind = row.total_days && row.total_days > 1 ? `${row.total_days} 天` : "一日游";
            if (blocked) {
              return (
                <tr key={row.product_id} data-testid={`wanle-blocked-${row.product_code}`} className="border-b border-slate-100 text-slate-400">
                  <td className="py-2.5">—</td>
                  <td><div className="font-semibold">{row.product_name}</div><div className="text-[12px]">{row.product_code} · {kind}</div></td>
                  <td colSpan={5}>多日游走「携程」页签</td>
                </tr>
              );
            }
            return (
              <Fragment key={row.product_id}>
                {bs.map((b) => (
                  <tr key={b.option_id} data-testid={`wanle-row-${b.option_code}`} className="border-b border-slate-100">
                    <td className="py-2.5">
                      <input type="checkbox" aria-label={`推送 ${b.option_code}`} checked={b.bound} disabled={!canManage || !b.bound || unbind.isPending}
                        onChange={() => { if (b.bound && b.mapping_id && window.confirm("取消后携程玩乐上这个票种的库存会被推成 0，已产生订单不受影响。确认取消？")) unbind.mutate(b); }} />
                    </td>
                    <td><div className="font-semibold text-slate-900">{row.product_name}</div><div className="text-[12px] text-slate-400">{row.product_code} · {kind}</div></td>
                    <td className="font-mono text-[12.5px]">{b.option_code}{b.option_name ? ` ${b.option_name}` : ""}</td>
                    <td className="font-mono text-[12px]">
                      {b.bound ? (
                        <span>产品 {b.wanle_product_id} · 套餐 {b.package_code} · PLU {b.plu}{b.child_plu ? ` · 儿童 ${b.child_plu}` : ""}
                          {canManage && <button type="button" data-testid={`wanle-edit-${b.option_code}`} onClick={() => setEditing({ row, b })} className="ml-2 font-sans text-[12px] text-blue-600 hover:underline">改</button>}</span>
                      ) : canManage ? (
                        <button type="button" data-testid={`wanle-bind-${b.option_code}`} onClick={() => setEditing({ row, b })} className="font-sans text-[12.5px] font-medium text-blue-600 hover:underline">填携程编号绑定</button>
                      ) : <span className="font-sans text-slate-400">未绑定</span>}
                    </td>
                    <td className="tabular-nums">{usd(row.price_min_cents)}{row.price_max_cents !== row.price_min_cents ? " 起" : ""}</td>
                    <td data-testid={`wanle-price-${b.option_code}`} className="tabular-nums">
                      {b.bound && b.wanle_price && b.wanle_price.settle_cents != null ? (
                        <>
                          <div>
                            {b.wanle_price.contract_mode === "sale" ? <>卖价 <b>{usd2(b.wanle_price.sale_cents)}</b>{b.wanle_price.settle_cents != null && <span className="text-slate-400"> ← 结算 {usd2(b.wanle_price.settle_cents)}</span>}</>
                              : <>结算 <b>{usd2(b.wanle_price.settle_cents)}</b>{b.wanle_price.sale_cents != null && <span className="text-slate-500"> → 卖价 {usd2(b.wanle_price.sale_cents)}</span>}</>}
                            <span className={`ml-1.5 rounded-full px-1.5 py-px text-[11px] ${b.wanle_price.source === "fixed" ? "bg-blue-50 text-blue-600" : "border border-slate-200 text-slate-500"}`} data-testid={`wanle-price-tag-${b.option_code}`}>{b.wanle_price.source === "fixed" ? "固定" : "自动"}</span>
                          </div>
                          <div className="text-[11.5px] text-slate-400">
                            {b.wanle_price.source === "fixed" ? `API 渠道价 ${usd2(b.wanle_price.api_settle_cents)} · 已单独给携程定价` : "= 售价中心 API 渠道价"}
                            {canPrice && <> · <button type="button" data-testid={`wanle-price-edit-${b.option_code}`} onClick={() => setPricing({ row, b })} className="text-blue-600 hover:underline">改价</button></>}
                          </div>
                          {b.wanle_price.mode_changed && b.wanle_price.source === "fixed" && <div className="text-[11px] text-amber-600">合同报价方式已改，固定价按佣金换算显示</div>}
                        </>
                      ) : b.bound ? (
                        <span className="text-slate-400">— {canPrice && b.wanle_price && <button type="button" data-testid={`wanle-price-edit-${b.option_code}`} onClick={() => setPricing({ row, b })} className="text-blue-600 hover:underline">改价</button>}</span>
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                    <td>
                      <span className={`rounded-full px-2 py-0.5 text-[11.5px] font-medium ${b.bound ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{b.bound ? "在售" : "未绑定"}</span>
                      {b.bound && <div className="text-[11.5px] text-slate-400">{b.sandbox_mode ? "沙箱测试中（自动推送暂停）" : when(b.last_push_at) ? `最近推送 ${when(b.last_push_at)}` : "等待首推"}</div>}
                    </td>
                  </tr>
                ))}
              </Fragment>
            );
          })}
          {rows.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-slate-400">没有可上架的一日游 / 门票产品</td></tr>}
        </tbody>
      </table>
      {editing && <WanleBindModal row={editing.row} b={editing.b} onClose={() => setEditing(null)} onDone={onChanged} />}
      {pricing && pricing.b.wanle_price && <WanlePriceDrawer row={pricing.row} b={pricing.b} onClose={() => setPricing(null)} onSaved={onChanged} />}
    </>
  );
}
